import { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, MicOff, PhoneOff, Radio } from 'lucide-react';
import { useCallProvider } from '@providers/CallProvider';

function formatDuration(seconds?: number | null) {
  if (!seconds || seconds < 1) {
    return '00:00';
  }
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const remainder = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${remainder}`;
}

export function CallTray() {
  const { activeCall, endLiveCall, toggleMute, clearCallError } = useCallProvider();
  const audioContainerRef = useRef<HTMLDivElement | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const container = audioContainerRef.current;
    if (!container || !activeCall?.remoteAudioTracks.length) {
      return;
    }

    const attachedElements = activeCall.remoteAudioTracks.map((track) => {
      const element = track.attach();
      element.autoplay = true;
      container.appendChild(element);
      return { track, element };
    });

    return () => {
      attachedElements.forEach(({ track, element }) => {
        track.detach(element);
        element.remove();
      });
    };
  }, [activeCall?.remoteAudioTracks]);

  useEffect(() => {
    if (!activeCall?.startedAt || activeCall.status !== 'connected') {
      return;
    }
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [activeCall?.startedAt, activeCall?.status]);

  const elapsedSeconds = useMemo(() => {
    if (!activeCall?.startedAt) {
      return 0;
    }
    return Math.max(Math.round((now - activeCall.startedAt) / 1000), 0);
  }, [activeCall?.startedAt, now]);

  if (!activeCall) {
    return null;
  }

  return (
    <aside className="call-tray">
      <div ref={audioContainerRef} className="visually-hidden" />
      <div className="call-tray-header">
        <div>
          <p className="hero-kicker">Live Call</p>
          <strong>{activeCall.call.to_number}</strong>
        </div>
        <span className="pill pill-soft">{activeCall.status}</span>
      </div>
      <div className="call-tray-body">
        <div className="call-tray-stat">
          <Radio size={16} />
          <span>{formatDuration(elapsedSeconds)}</span>
        </div>
        <div className="call-tray-stat">
          <span>{activeCall.call.recording_status}</span>
        </div>
      </div>
      {activeCall.errorMessage ? (
        <div className="call-tray-error">
          <p>{activeCall.errorMessage}</p>
          <button type="button" className="button button-secondary" onClick={clearCallError}>
            Dismiss
          </button>
        </div>
      ) : null}
      <div className="call-tray-actions">
        <button type="button" className="button button-secondary" onClick={() => void toggleMute()} disabled={activeCall.status !== 'connected'}>
          {activeCall.isMuted ? <MicOff size={16} /> : <Mic size={16} />}
          {activeCall.isMuted ? 'Unmute' : 'Mute'}
        </button>
        <button type="button" className="button button-danger" onClick={() => void endLiveCall()}>
          <PhoneOff size={16} />
          End
        </button>
      </div>
    </aside>
  );
}
